import React from "react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Menu, Bell, PanelLeft, LogOut, UserCircle, Building2 } from "lucide-react";

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

export function Header({ onMobileMenu, onToggleSidebar }) {
  const { user, logout } = useAuth();
  const org = user?.organization;

  return (
    <header
      className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md sm:px-6"
      data-testid="app-header"
    >
      {/* Mobile menu */}
      <button
        onClick={onMobileMenu}
        className="rounded-md p-2 text-slate-500 hover:bg-slate-100 lg:hidden"
        data-testid="mobile-menu-button"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Desktop collapse toggle */}
      <button
        onClick={onToggleSidebar}
        className="hidden rounded-md p-2 text-slate-500 hover:bg-slate-100 lg:inline-flex"
        data-testid="header-toggle-sidebar"
        aria-label="Toggle sidebar"
      >
        <PanelLeft className="h-5 w-5" />
      </button>

      {/* Organization context */}
      <div className="flex items-center gap-2" data-testid="header-organization">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-50 text-emerald-600">
          <Building2 className="h-4 w-4" />
        </div>
        <div className="hidden sm:block leading-tight">
          <p className="text-sm font-semibold text-slate-900" data-testid="header-organization-name">{org?.name || "—"}</p>
          <p className="text-xs text-slate-400">{org?.organization_type || "Healthcare"}</p>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        {/* Notifications placeholder */}
        <button
          className="relative rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
          data-testid="notifications-button"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white" />
        </button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-auto gap-2 px-2 py-1.5 hover:bg-slate-100" data-testid="user-menu-trigger">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.avatar_url} alt={user?.name} />
                <AvatarFallback className="bg-emerald-100 text-xs font-semibold text-emerald-700">
                  {initials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden text-left leading-tight md:block">
                <p className="text-sm font-medium text-slate-900" data-testid="header-user-name">{user?.name}</p>
                <p className="text-xs text-slate-400" data-testid="header-user-role">{user?.role_label}</p>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-slate-900">{user?.name}</span>
              <span className="text-xs font-normal text-slate-400">{user?.email}</span>
              <Badge variant="secondary" className="mt-1 w-fit bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
                {user?.role_label}
              </Badge>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" data-testid="menu-profile">
              <UserCircle className="h-4 w-4" /> Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="cursor-pointer text-red-600 focus:text-red-600"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
